import type { CartItem } from '../store/usePosStore';
import {
  POS_ACTIVE_DRAFT_TTL_MS,
  POS_CART_PERSISTENCE_SCHEMA_VERSION,
  type PosCartPersistenceScope,
  type PosCartPromoSnapshot,
  type PosCartSnapshot,
} from '../domain/posParkedSaleTypes';

export function createCartSnapshot(input: {
  scope: PosCartPersistenceScope;
  cart: CartItem[];
  promo?: PosCartPromoSnapshot;
  savedAt?: string;
}): PosCartSnapshot {
  return {
    schemaVersion: POS_CART_PERSISTENCE_SCHEMA_VERSION,
    organizationId: input.scope.organizationId,
    branchId: input.scope.branchId,
    userId: input.scope.userId,
    savedAt: input.savedAt ?? new Date().toISOString(),
    cart: input.cart,
    promo: input.promo,
  };
}

function isCartItemShape(value: unknown): value is CartItem {
  if (!value || typeof value !== 'object') return false;
  const row = value as CartItem;
  return (
    typeof row.lineId === 'string' &&
    typeof row.productId === 'string' &&
    typeof row.name === 'string' &&
    typeof row.price === 'number' &&
    typeof row.quantity === 'number' &&
    (row.itemType === 'product' ||
      row.itemType === 'custom' ||
      (row.itemType === 'package' && typeof row.packageId === 'string'))
  );
}

export function parseCartSnapshot(
  raw: string | null,
  scope: PosCartPersistenceScope,
  options?: { applyTtl?: boolean }
): PosCartSnapshot | null {
  if (!raw?.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as PosCartSnapshot;
    if (parsed.schemaVersion !== POS_CART_PERSISTENCE_SCHEMA_VERSION) return null;
    if (
      parsed.organizationId !== scope.organizationId ||
      parsed.branchId !== scope.branchId ||
      parsed.userId !== scope.userId
    ) {
      return null;
    }
    if (!Array.isArray(parsed.cart) || parsed.cart.length === 0) return null;
    if (!parsed.cart.every(isCartItemShape)) return null;
    const savedAtMs = Date.parse(parsed.savedAt);
    if (!Number.isFinite(savedAtMs)) return null;
    // F-10: parked sales have no TTL — only active drafts expire after 48h.
    const applyTtl = options?.applyTtl ?? true;
    if (applyTtl && Date.now() - savedAtMs > POS_ACTIVE_DRAFT_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function canParkCart(cart: CartItem[]): boolean {
  return cart.length > 0;
}

export function snapshotCartSummary(snapshot: PosCartSnapshot): { lineCount: number; total: number } {
  const total = snapshot.cart.reduce((sum, line) => sum + line.price * line.quantity, 0);
  return { lineCount: snapshot.cart.length, total };
}
